import { Component, OnDestroy, OnInit, viewChild } from '@angular/core';
import { ApolloService } from '@src/app/services/apollo/apollo.service';
import { MatDialog } from '@angular/material/dialog';
import { JsonModalComponent } from '@src/app/components/modals/json-modal/json-modal.component';
import { JsonModalDataObject } from '@src/app/model/json-modal-data-object';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { PortalAttribute } from '@src/app/model/portal-attribute';
import { feSortToBeSort } from '@src/app/components/utils/utils';
import { MatSort, Sort } from '@angular/material/sort';
import { JsonModal } from '@src/app/components/utils/json-modal';
import { TypedFormModal } from '@src/app/components/utils/typed-form-modal';
import { TypedFormModalDataObject } from '@src/app/model/typed-form-modal-data-object';
import { AttributeInput, SubjectType } from '@src/app/generated/globalTypes';
import { fixedEnvironment } from '@src/environments/environment.fixed';

@Component({
  selector: 'app-attribute-list',
  templateUrl: './attribute-list.component.html',
  styleUrls: ['./attribute-list.component.scss'],
  standalone: false,
})
export class AttributeListComponent implements OnInit, OnDestroy {
  private _componentDestroyed$: Subject<void> = new Subject();

  public displayedColumns: string[] = ['id', 'name', 'attributeType', 'title', 'action'];
  public currentAttributeItems: PortalAttribute[];
  public hasNextPage: boolean;
  public pageSize: number = 10;
  public currentPage: number = 0;
  public count: number = 0;

  private _attributeFailMsg: string = 'atributes.upload.failure';
  private _skipFields: string[] = ['id'];

  readonly sort = viewChild(MatSort);

  constructor(
    private _apolloService: ApolloService,
    private _dialog: MatDialog,
    private _translateService: TranslateService
  ) {
  }

  public ngOnInit(): void {
    this._apolloService.getAllAttributes$()
      .pipe(takeUntil(this._componentDestroyed$))
      .subscribe((pagedData) => {
        this.currentAttributeItems = pagedData.attributes;
        this.count = pagedData.count;
        this.hasNextPage = pagedData.hasNextPage;
      });
  }

  public paginate(toPage: number): void {
    this.currentPage = toPage;
    this._getAttributes(this.pageSize, this.currentPage);
  }

  public setPageSize(newPageSize: string): void {
    // the new page size is a string because of the select component; if we somehow get a non-integer value, ignore it and reset the value.
    const pageSize = Number(newPageSize);
    if (pageSize > 0) {
      this.pageSize = pageSize;
      this.currentPage = 0;
      this._getAttributes(this.pageSize, this.currentPage);
    } else {
      console.error('Received non-integer value for page size');
    }
  }

  public openAddAttributeDialog(): void {
    const dialogRef = JsonModal.openJSONModalDialog(this._dialog, new JsonModalDataObject(
      'attribute',
      new PortalAttribute(),
      'attributes.upload.header',
      'buttons.submit',
    ));
    JsonModal.useJsonDialog(
      dialogRef,
      this._apolloService.newAttribute$.bind(this._apolloService),
      this._translateService.instant(this._attributeFailMsg),
      this._apolloService.getAllAttributesRefresh.bind(this._apolloService)
    );
  }

  public openEditAttributeJsonDialog(attribute: PortalAttribute): void {
    const dialogRef = this._dialog.open(JsonModalComponent, {
      data: new JsonModalDataObject(
        'attribute',
        attribute ? attribute : new PortalAttribute(),
        'attributes.upload.header',
        'buttons.submit'
      ),
      minWidth: fixedEnvironment.jsonModalWidthLarge,
    });
    const update = json => this._apolloService.updateAttribute$(attribute.id, json);
    JsonModal.useJsonDialog(
      dialogRef,
      update.bind(this._apolloService),
      this._translateService.instant(this._attributeFailMsg),
      this._apolloService.getAllAttributesRefresh.bind(this._apolloService)
    );
  }

  public openEditAttributeFormDialog(attribute: PortalAttribute): void {
    const dialogRef = TypedFormModal.openTypedFormModalDialog(
      this._dialog, new TypedFormModalDataObject(
        'attribute',
        SubjectType.AttributesType,
        attribute,
        attribute.id,
        this._skipFields,
      )
    );
    TypedFormModal.useFormDialog(
      dialogRef,
      (
        (edited: AttributeInput) =>
          this._apolloService.updateAttribute$(attribute.id, PortalAttribute.toJsonInput(edited))
      ).bind(this),
      this._translateService.instant(this._attributeFailMsg),
      this._apolloService.getAllAttributesRefresh.bind(this._apolloService)
    );
  }

  private _getAttributes(pageSize: number, currentPage: number, sort?: Sort): void {
    const beSort = sort ? feSortToBeSort(sort) : undefined;
    this._apolloService.getAllAttributes$(pageSize, pageSize * currentPage, beSort ? [beSort] : undefined)
      .pipe(takeUntil(this._componentDestroyed$))
      .subscribe((pagedData) => {
        this.currentAttributeItems = pagedData.attributes;
        this.count = pagedData.count;
        this.hasNextPage = pagedData.hasNextPage;
      });
  }

  public sortChange(sortState: Sort): void {
    this._getAttributes(this.pageSize, this.currentPage, sortState);
  }

  public ngOnDestroy(): void {
    this._componentDestroyed$.next();
    this._componentDestroyed$.complete();
  }
}
